import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaperPlaneTilt, Handshake, Trophy, CheckCircle, Circle, LinkSimple, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US");
const empty = { gc_name: "", company: "", email: "", bid_due_date: "", message: "" };

export default function BiddingTab({ projectId, onChange }) {
  const [invites, setInvites] = useState([]);
  const [award, setAward] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [awardOpen, setAwardOpen] = useState(false);
  const [awardForm, setAwardForm] = useState({ invitation_id: "", contract_value: "", notice_to_proceed: "" });

  const load = async () => {
    const inv = await api.get(`/projects/${projectId}/gc-invitations`);
    setInvites(inv.data.invitations);
    const bids = await api.get(`/projects/${projectId}/bids`);
    setAward(bids.data.award);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/gc-invitations`, form);
      const emailStatus = data.email_result?.status;
      toast.success(emailStatus === "sent" ? "Invitation email sent" : "GC invited (email not configured — link ready)");
      setOpen(false); setForm(empty); await load(); onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/gc-invite/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  const openAward = (inv) => {
    setAwardForm({ invitation_id: inv.id, contract_value: inv.bid?.bid_amount || "", notice_to_proceed: "" });
    setAwardOpen(true);
  };

  const submitAward = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/award`, { ...awardForm, contract_value: parseFloat(awardForm.contract_value) || 0 });
      toast.success("GC awarded");
      setAwardOpen(false); await load(); onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const withBids = invites.filter((i) => i.bid);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-primary mb-1">Phase 3 · General Contractor Bidding</div>
          <h2 className="font-heading font-bold text-xl tracking-tight">Bid Solicitation</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-gc-button" className="rounded-none bg-primary hover:bg-primary/90 text-white gap-2">
              <PaperPlaneTilt size={18} weight="bold" /> Invite GC
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Invite General Contractor</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="gc-invite-form">
              <div className="space-y-2"><Label className="overline">GC Name *</Label><Input value={form.gc_name} onChange={set("gc_name")} required data-testid="gc-name-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Company</Label><Input value={form.company} onChange={set("company")} data-testid="gc-company-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Email *</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="gc-email-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Bid Due Date</Label><Input type="date" value={form.bid_due_date} onChange={set("bid_due_date")} data-testid="gc-duedate-input" className="rounded-none h-11" /></div>
              <div className="space-y-2"><Label className="overline">Message</Label><Textarea value={form.message} onChange={set("message")} data-testid="gc-message-input" className="rounded-none" rows={2} /></div>
              <Button type="submit" disabled={busy} data-testid="gc-invite-submit" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">
                {busy ? "Sending…" : "Send Invitation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {award && (
        <div className="border border-primary bg-primary/5 p-5 mb-6 flex items-center gap-4" data-testid="award-banner">
          <Trophy size={32} weight="fill" className="text-primary" />
          <div>
            <div className="overline text-primary">Awarded Contract</div>
            <div className="font-heading font-bold text-lg">{award.winning_gc} {award.company && `· ${award.company}`}</div>
            <div className="text-sm text-muted-foreground">Contract Value: {fmtMoney(award.contract_value)} · NTP: {award.notice_to_proceed || "TBD"}</div>
          </div>
        </div>
      )}

      {/* Invitations */}
      {invites.length ? (
        <div className="border border-border bg-card mb-8">
          <div className="overline text-muted-foreground px-4 pt-4">Invited General Contractors</div>
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>GC</TableHead><TableHead>Email</TableHead><TableHead>Due</TableHead>
              <TableHead>Opened</TableHead><TableHead>Status</TableHead><TableHead>Link</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invites.map((inv) => (
                <TableRow key={inv.id} className="even:bg-muted/20" data-testid={`invite-row-${inv.id}`}>
                  <TableCell className="font-medium">{inv.gc_name}<div className="text-xs text-muted-foreground">{inv.company}</div></TableCell>
                  <TableCell className="text-muted-foreground">{inv.email}</TableCell>
                  <TableCell className="text-muted-foreground">{inv.bid_due_date || "—"}</TableCell>
                  <TableCell>{inv.opened ? <CheckCircle size={18} weight="fill" className="text-primary" /> : <Circle size={18} className="text-muted-foreground" />}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 bg-secondary">{inv.status}</span></TableCell>
                  <TableCell><button onClick={() => copyLink(inv.token)} data-testid={`copy-link-${inv.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-150 flex items-center gap-1"><Copy size={16} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-card border border-border border-dashed p-12 text-center mb-8">
          <Handshake size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="text-muted-foreground">No GCs invited yet. Invite contractors to distribute plans and collect bids.</p>
        </div>
      )}

      {/* Bid comparison */}
      {withBids.length > 0 && (
        <div>
          <div className="overline text-muted-foreground mb-2">Bid Comparison</div>
          <div className="border border-border bg-card overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>GC</TableHead><TableHead className="text-right">Bid Amount</TableHead>
                <TableHead>Schedule</TableHead><TableHead>Clarifications</TableHead>
                <TableHead>Value Engineering</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {withBids.map((inv) => (
                  <TableRow key={inv.id} className="even:bg-muted/20" data-testid={`bid-row-${inv.id}`}>
                    <TableCell className="font-medium">{inv.gc_name}<div className="text-xs text-muted-foreground">{inv.company}</div></TableCell>
                    <TableCell className="text-right font-heading font-bold">{fmtMoney(inv.bid.bid_amount)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{inv.bid.schedule || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{inv.bid.clarifications || "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{inv.bid.value_engineering || "—"}</TableCell>
                    <TableCell>
                      {award?.invitation_id === inv.id ? (
                        <span className="text-xs px-2 py-1 bg-primary text-white flex items-center gap-1 w-fit"><Trophy size={12} weight="fill" /> Awarded</span>
                      ) : (
                        <Button onClick={() => openAward(inv)} data-testid={`award-button-${inv.id}`} size="sm" className="rounded-none bg-primary hover:bg-primary/90 text-white text-xs">Award</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={awardOpen} onOpenChange={setAwardOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-heading font-black tracking-tight">Award Contract</DialogTitle></DialogHeader>
          <form onSubmit={submitAward} className="space-y-4" data-testid="award-form">
            <div className="space-y-2"><Label className="overline">Contract Value *</Label><Input type="number" value={awardForm.contract_value} onChange={(e) => setAwardForm({ ...awardForm, contract_value: e.target.value })} required data-testid="award-value-input" className="rounded-none h-11" /></div>
            <div className="space-y-2"><Label className="overline">Notice to Proceed</Label><Input type="date" value={awardForm.notice_to_proceed} onChange={(e) => setAwardForm({ ...awardForm, notice_to_proceed: e.target.value })} data-testid="award-ntp-input" className="rounded-none h-11" /></div>
            <Button type="submit" data-testid="award-submit-button" className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white">Confirm Award</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
